import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAllergyMutations } from '@/features/patient-record/hooks/useAllergyMutations';
import type {
  AgeOfOnsetCategory,
  AllergyProfileViewModel,
  AllergyViewModel,
} from '@/features/patient-record/types';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';
import { AllergyForm } from './AllergyForm';

interface AllergiesCardProps {
  readonly guid: string;
  readonly profile: AllergyProfileViewModel;
}

/**
 * Compose an allergy's age-of-onset into a compact "Category · N y" label (either part optional),
 * or null when neither is recorded. Shared by the list row and the collapsed summary.
 */
function formatAllergyOnset(allergy: AllergyViewModel, t: TFunction): string | null {
  const parts: string[] = [];
  if (allergy.ageOfOnsetCategory !== null) {
    parts.push(t(`patient.medicalHistory.allergies.onsetCategory.${allergy.ageOfOnsetCategory}`));
  }
  if (allergy.ageOfOnsetYears !== null) {
    parts.push(
      t('patient.medicalHistory.allergies.onsetYearsValue', { value: allergy.ageOfOnsetYears }),
    );
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * Allergy profile card (PTV2-1112). NKDA toggle sits in the header
 * action slot; when on, the per-allergy add affordance is hidden but
 * existing rows remain in the cache (and on the server) so toggling
 * NKDA off restores the list.
 *
 * Add/edit open the shared `AllergyForm` modal — 'create' opens a blank
 * dialog; a row opens the same dialog prefilled.
 */
export function AllergiesCard({
  guid,
  profile,
}: AllergiesCardProps): ReactElement {
  const { t } = useTranslation();
  const { setNkda, removeAllergy } = useAllergyMutations(guid);
  const [editing, setEditing] = useState<'create' | AllergyViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AllergyViewModel | null>(null);

  const allergies = [...profile.allergies].sort((a, b) =>
    a.allergen.localeCompare(b.allergen),
  );
  const editingRecord =
    editing !== null && editing !== 'create' ? editing : undefined;

  const summaryRows: CollapsibleCardSummaryRow[] = profile.nkda
    ? [
        {
          label: t('patient.medicalHistory.allergies.nkda', { defaultValue: 'No known drug allergies' }),
          value: t('common.yes', { defaultValue: 'Yes' }),
          filled: true,
        },
      ]
    : allergies.map((a) => ({
        label: a.allergen,
        value: formatAllergyOnset(a, t) ?? '—',
        filled: true,
      }));

  // Count sits to the *right* of the NKDA toggle by convention — every
  // patient-record card pins the count badge to the rightmost edge of
  // the header (mirrors MeasurementsCard's metric/imperial layout). The Add
  // affordance sits in the header too, matching the other record sub-sections
  // (Add diagnosis, Add variant, …); it's hidden while NKDA is on.
  const headerAction = (
    <div className="flex items-center gap-2">
      {!profile.nkda && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing('create');
          }}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          {t('patient.medicalHistory.allergies.add')}
        </Button>
      )}
      <NkdaToggle
        value={profile.nkda}
        pending={setNkda.isPending}
        onChange={(next) => {
          setNkda.mutate(next);
          if (next) setEditing(null);
        }}
      />
      {!profile.nkda && (
        <span
          className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-xs font-medium tabular-nums"
          aria-label={`${String(profile.allergies.length)} items`}
        >
          {profile.allergies.length}
        </span>
      )}
    </div>
  );

  return (
    <>
      <CollapsibleCard
        idPrefix="medical-history"
        anchorId="allergies"
        title={t('patient.medicalHistory.allergies.heading')}
        defaultOpen
        headerAction={headerAction}
        summary={summaryRows}
      >
        {profile.nkda ? (
          <p className="text-sm text-muted-foreground italic">
            {t('patient.medicalHistory.allergies.nkdaActive')}
          </p>
        ) : allergies.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            {t('patient.medicalHistory.allergies.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-border/50 rounded-md border border-border/60">
            {allergies.map((allergy) => (
              <AllergyRow
                key={allergy.id}
                allergy={allergy}
                isEditing={editingRecord?.id === allergy.id}
                onEdit={() => {
                  setEditing(allergy);
                }}
                onDelete={() => {
                  setPendingDelete(allergy);
                }}
              />
            ))}
          </ul>
        )}
      </CollapsibleCard>

      {editing !== null && (
        <AllergyForm
          guid={guid}
          {...(editingRecord !== undefined ? { record: editingRecord } : {})}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.medicalHistory.allergies.deleteConfirmTitle')}
          message={t('patient.medicalHistory.allergies.deleteConfirmMessage', {
            allergen: pendingDelete.allergen,
          })}
          confirmLabel={t('patient.medicalHistory.allergies.delete')}
          busy={removeAllergy.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            removeAllergy.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

interface AllergyRowProps {
  readonly allergy: AllergyViewModel;
  readonly isEditing: boolean;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function AllergyRow({
  allergy,
  isEditing,
  onEdit,
  onDelete,
}: AllergyRowProps): ReactElement {
  const { t } = useTranslation();
  const onsetLabel = formatAllergyOnset(allergy, t);
  return (
    <li
      className={`flex items-start gap-3 px-3 py-2 text-sm ${
        isEditing ? 'bg-muted/30' : 'hover:bg-muted/20'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-card-foreground">{allergy.allergen}</span>
          {onsetLabel !== null && (
            <span className="text-xs text-muted-foreground">{onsetLabel}</span>
          )}
        </div>
        {allergy.notes !== null && (
          <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap break-words">
            {allergy.notes}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          aria-label={t('patient.medicalHistory.allergies.edit')}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          aria-label={t('patient.medicalHistory.allergies.delete')}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        </Button>
      </div>
    </li>
  );
}

interface NkdaToggleProps {
  readonly value: boolean;
  readonly pending: boolean;
  readonly onChange: (next: boolean) => void;
}

/**
 * NKDA toggle. Rendered in the card header so the operator can mark
 * "no known drug allergies" without scrolling past the list. We use
 * a checkbox bound to a label (rather than a switch) for a11y — it
 * announces the boolean state cleanly and matches the rest of the
 * patient-record card chrome.
 */
function NkdaToggle({ value, pending, onChange }: NkdaToggleProps): ReactElement {
  const { t } = useTranslation();
  const id = 'allergy-nkda-toggle';
  return (
    <label
      htmlFor={id}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground cursor-pointer select-none"
    >
      <input
        id={id}
        type="checkbox"
        checked={value}
        disabled={pending}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        className="h-4 w-4 rounded border-input text-primary focus:ring-1 focus:ring-ring"
      />
      <span>{t('patient.medicalHistory.allergies.nkdaLabel')}</span>
    </label>
  );
}

export type { AgeOfOnsetCategory };
